// src/components/documents/DocumentDetailModal.tsx
import { useDocumentStore } from "@/store/documentStore"
import { useNotificationStore } from "@/store/notificationStore"
import { useAuthStore } from "@/store/authStore"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { format } from "date-fns"
import { ru } from "date-fns/locale"
import { CheckCircle2, RotateCw, Download, FileText, Info } from "lucide-react"
import { useState, useEffect } from "react"
import { toast } from "@/hooks/use-toast"
import DocumentChat from "./DocumentChat"
import confetti from "canvas-confetti"

// === ДИЗАЙН-СЛОЙ ===
import { documentDetailModalTheme } from "./ui/DocumentDetailModalUI"

interface DocumentDetailModalProps {
  docId: string
  onClose: () => void
}

export default function DocumentDetailModal({ docId, onClose }: DocumentDetailModalProps) {
  const { documents, approve, reject } = useDocumentStore()
  const { markDocumentAsRead } = useNotificationStore()
  const { user } = useAuthStore()

  const doc = documents.find((d) => d.id === docId)

  const [isProcessing, setIsProcessing] = useState(false)

  const currentUserId = String(user?.id || user?.uid || "")

  const isSpecialUser = currentUserId === "6" || currentUserId === "7"

  useEffect(() => {
    if (docId) {
      markDocumentAsRead(docId)
    }
  }, [docId, markDocumentAsRead])

  const safeFormatDate = (dateValue: any): string => {
    if (!dateValue) return "—";
    try {
      const date = dateValue instanceof Date 
        ? dateValue 
        : dateValue?.toDate 
          ? dateValue.toDate() 
          : new Date(dateValue);
      
      if (isNaN(date.getTime())) return "—";
      return format(date, "dd MMMM yyyy, HH:mm", { locale: ru });
    } catch {
      return "—";
    }
  };

  if (!doc) {
    return <div className="p-10 text-center text-purple-400">Документ не найден</div>
  }

  const handleApprove = async () => {
    if (isProcessing || isSpecialUser) return
    setIsProcessing(true)

    try {
      await approve(docId)
      await new Promise((resolve) => setTimeout(resolve, 800))
      onClose()
      confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } })
    } catch (err) {
      console.error("Ошибка approve:", err)
      toast({ title: "Ошибка", description: "Не удалось согласовать", variant: "destructive" })
    } finally {
      setIsProcessing(false)
    }
  }

  const handleReturnForRework = async () => {
    if (isProcessing || isSpecialUser) return
    setIsProcessing(true)
    try {
      await reject(docId, "На доработку")
      await new Promise((resolve) => setTimeout(resolve, 800))
      onClose()
    } catch (err) {
      console.error("Ошибка возврата на доработку:", err)
    } finally {
      setIsProcessing(false)
    }
  }

  const canAct = doc.status === "pending" && 
                 !isSpecialUser && 
                 doc.approvers[doc.currentApproverIndex] === currentUserId

  return (
    <div className={documentDetailModalTheme.mainContainer}>
      <div className={documentDetailModalTheme.header}>
        <div className="flex items-center gap-4">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-md">
            <FileText className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-white tracking-tight">
              {doc.title}
            </h2>
            <p className="text-sm text-purple-200/90 mt-0.5">
              Создан: {safeFormatDate(doc.createdAt)}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className={documentDetailModalTheme.sidebar}>
          <div className="space-y-5">
            <div className={documentDetailModalTheme.section}>
              <div className="flex items-center gap-2 text-purple-300 text-sm mb-2">
                <Info className="h-3.5 w-3.5" />
                <span className="font-medium">Описание</span>
              </div>
              <p className="text-purple-100 text-sm leading-relaxed whitespace-pre-line">
                {doc.description || "Описание отсутствует"}
              </p>
            </div>

            {doc.fileUrl && (
              <div className={documentDetailModalTheme.section}>
                <div className="flex items-center gap-2 text-purple-300 text-sm mb-2">
                  <FileText className="h-3.5 w-3.5" />
                  <span className="font-medium">Файл документа</span>
                </div>
                <div className={documentDetailModalTheme.fileCard}>
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="bg-gradient-to-br from-fuchsia-600 to-purple-600 rounded-xl p-3 shadow-md flex-shrink-0">
                      <FileText className="h-6 w-6 text-white" />
                    </div>
                    <span className="text-white text-base font-medium truncate flex-1 min-w-0">
                      {doc.fileName || "Документ"}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 text-white hover:bg-white/15 rounded-full transition-colors flex-shrink-0"
                    onClick={() => window.open(doc.fileUrl, "_blank")}
                  >
                    <Download className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            )}

            <div className={documentDetailModalTheme.section}>
              <div className="flex items-center gap-2 text-purple-300 text-sm mb-2">
                <span className="font-medium">Текущий статус</span>
              </div>
              <Badge className={documentDetailModalTheme.statusBadge}>
                {doc.status === "approved" ? "Согласован" :
                 doc.status === "rejected" ? "Отклонён" : "Ожидает согласования"}
              </Badge>
            </div>

            {canAct && (
              <div className="pt-4">
                <div className={documentDetailModalTheme.actionButtons}>
                  <Button
                    onClick={handleApprove}
                    disabled={isProcessing}
                    className={documentDetailModalTheme.approveButton}
                  >
                    {isProcessing ? "Обработка..." : (
                      <>
                        <CheckCircle2 className="mr-3 h-6 w-6" />
                        Согласовать
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={handleReturnForRework}
                    disabled={isProcessing}
                    variant="destructive"
                    className={documentDetailModalTheme.reworkButton}
                  >
                    {isProcessing ? "Обработка..." : (
                      <>
                        <RotateCw className="mr-3 h-6 w-6" />
                        На доработку
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={documentDetailModalTheme.chatContainer}>
          <DocumentChat docId={docId} />
        </div>
      </div>
    </div>
  )
}